/**
 * Connect Block Renderer
 *
 * Renders a "Connectors that could help" card: the relevant third-party apps
 * the user can connect for the current request, each with a Connect button (or
 * a "Use" button once connected), plus a footer to browse all connectors or
 * dismiss the suggestions.
 *
 * The LLM only supplies each app's `short_name` (via `ui_connect`); this
 * renderer enriches with live app metadata (`useApps`) and connection status
 * (`useCredentials`), so a Connect button flips to "Use" reactively the moment
 * the credential is created — no re-fetch wiring needed.
 *
 * There is no automatic retry: "Use" emits a `connect` UI action that
 * `chat.tsx` sends as a normal user PROMPT ("Connected X — continue"), and
 * "None of these" sends a dismiss PROMPT. Either way the model proceeds on the
 * next turn now that the app's tools are (or aren't) loaded.
 *
 * The terminal choice (which app was used, or a dismissal) is persisted to the
 * chat row's `behaviour_action_state`, so on reload the card renders settled
 * instead of re-offering buttons the user already acted on. Live connection
 * status stays reactive (read from `useCredentials`, never persisted).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button } from "@strivelabs/ui-core";
import { Plug, Check, Loader2 } from "lucide-react";
import { AppLogo } from "@/components/AppLogo";
import { useApps } from "@/hooks/useApps";
import { useCredentials } from "@/hooks/useConnections";
import { useConnectionFlow } from "@/hooks/useConnectionFlow";
import { useChatMetadata } from "@/contexts/ChatMetadataContext";
import { updateBehaviourActionState } from "@/services/DataService";
import { BasicAuthConnectModal } from "@/components/settings/BasicAuthConnectModal";
import { ApiKeyConnectModal } from "@/components/settings/ApiKeyConnectModal";
import { GoogleAdsConfigDialog } from "@/components/settings/GoogleAdsConfigDialog";
import { GoogleAnalyticsConfigDialog } from "@/components/settings/GoogleAnalyticsConfigDialog";
import { GoogleSearchConsoleConfigDialog } from "@/components/settings/GoogleSearchConsoleConfigDialog";
import type { App } from "@/services/DataService";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";

const CONNECTIONS_ROUTE = "/ui/settings?tab=connections";

/** Hard cap on how many connectors the card offers, regardless of how many the model passes. */
const MAX_CONNECTORS = 3;

/** Key under which the card's terminal choice is stored in `behaviour_action_state`. */
const CONNECT_STATE_KEY = "connect";

/** User-facing label for each app's connect method, shown as a badge on the card. */
const AUTH_METHOD_LABELS: Record<string, string> = {
  oauth: "OAuth",
  api_key: "API key",
  basic: "Basic auth",
};

/** The card's persisted terminal choice: an app was used, or the suggestions were dismissed. */
type ConnectResolution = { used?: string; dismissed?: boolean };

export interface ConnectBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** Chat row id — needed to persist the terminal choice to `behaviour_action_state`. */
  chatHistoryId?: string;
  /** Persisted per-row behaviour state; carries this card's prior `used`/`dismissed` choice on reload. */
  behaviourActionState?: Record<string, unknown>;
}

/**
 * The card stays interactive even when rendered from history (chat.tsx passes
 * `readOnly` to historical blocks). Unlike one-shot widgets (select /
 * confirmation), connecting an app is valid at any point — including after a
 * page refresh — so this renderer intentionally ignores read-only, and instead
 * settles only once the user has explicitly used or dismissed a suggestion.
 */
export function ConnectBlockRenderer({
  block,
  onUIAction,
  chatHistoryId,
  behaviourActionState,
}: ConnectBlockRendererProps) {
  const navigate = useNavigate();
  const { workflowInstanceId, workflowExecutionId } = useChatMetadata();
  const { data: appsData } = useApps();
  const { data: credentialsData } = useCredentials();

  const apps = useMemo(() => appsData ?? [], [appsData]);

  const connectedShortNames = useMemo(() => {
    const rows = credentialsData?.credentials ?? [];
    return new Set(
      rows
        .filter((row) => row.status === "ACTIVE")
        .map((row) => row.app_name)
    );
  }, [credentialsData]);

  const [resolution, setResolution] = useState<ConnectResolution>(
    (behaviourActionState?.[CONNECT_STATE_KEY] as ConnectResolution | undefined) ?? {}
  );

  const persist = useCallback(
    (next: ConnectResolution) => {
      setResolution(next);
      if (workflowInstanceId && workflowExecutionId && chatHistoryId) {
        void updateBehaviourActionState(
          workflowInstanceId, workflowExecutionId, chatHistoryId, CONNECT_STATE_KEY, next
        );
      }
    },
    [workflowInstanceId, workflowExecutionId, chatHistoryId]
  );

  const {
    postConnectionConfig,
    handleConnectApp,
    connectAppByShortName,
    handleConfigComplete,
    isConnecting,
    connectingApp,
    basicAuthApp,
    closeBasicAuthModal,
    handleBasicAuthSuccess,
    apiKeyApp,
    closeApiKeyModal,
    handleApiKeySuccess,
  } = useConnectionFlow({ apps });

  const [pendingConnectShortName, setPendingConnectShortName] = useState<string | null>(null);

  /**
   * OAuth connects open in a separate browser tab, so the hook's in-memory
   * `isConnecting` flips back to false — and this tab is backgrounded — before
   * the user returns, leaving the button showing a plain "Connect" with no sign
   * the flow is underway. Remember the click here and keep the button in its
   * connecting state until the credential reports connected. Modal-based auth
   * (basic / api key) already has its own in-view feedback, so it's excluded.
   */
  const handleConnect = useCallback(
    (app: App) => {
      const authType = app.auth?.[0]?.type;
      if (authType !== "basic" && authType !== "api_key") {
        setPendingConnectShortName(app.short_name);
      }
      handleConnectApp(app);
    },
    [handleConnectApp]
  );

  useEffect(() => {
    if (pendingConnectShortName && connectedShortNames.has(pendingConnectShortName)) {
      setPendingConnectShortName(null);
    }
  }, [pendingConnectShortName, connectedShortNames]);

  const handleUse = useCallback(
    (app: App) => {
      persist({ used: app.short_name });
      onUIAction({
        type: "ui_action",
        component: "connect",
        label: app.display_name,
        state: { action: "use", short_name: app.short_name, display_name: app.display_name },
      });
    },
    [onUIAction, persist]
  );

  const handleDismiss = useCallback(() => {
    persist({ dismissed: true });
    onUIAction({ type: "ui_action", component: "connect", state: { action: "dismiss" } });
  }, [onUIAction, persist]);

  if (block.type !== "connect") return null;
  const { content } = block;

  const resolved = content.apps
    .map((ref) => {
      const app = apps.find((candidate) => candidate.short_name === ref.short_name);
      return app ? { app, reason: ref.reason ?? app.description } : null;
    })
    .filter((entry): entry is { app: App; reason: string } => entry !== null)
    .slice(0, MAX_CONNECTORS);

  const isResolved = resolution.dismissed || Boolean(resolution.used);

  /**
   * While `apps` metadata is still loading — or if the model passed app names
   * that don't resolve — `resolved` is empty. Rendering the card then leaves
   * only a header and footer with no connectors, which looks broken, so bail
   * until there's something to offer. A settled card still renders (it shows
   * the terminal choice, not connector rows).
   */
  if (!isResolved && resolved.length === 0) {
    return null;
  }

  const usedApp = resolution.used
    ? apps.find((candidate) => candidate.short_name === resolution.used)
    : undefined;

  const configDialogs: Record<string, React.ReactNode> = postConnectionConfig
    ? {
        google_ads: (
          <GoogleAdsConfigDialog
            open
            config={postConnectionConfig}
            onComplete={handleConfigComplete}
            onConnectGoogleAds={() => connectAppByShortName("google_ads")}
          />
        ),
        google_ads_dataset: (
          <GoogleAdsConfigDialog
            open
            config={postConnectionConfig}
            onComplete={handleConfigComplete}
            onConnectGoogleAds={() => connectAppByShortName("google_ads")}
          />
        ),
        google_analytics: (
          <GoogleAnalyticsConfigDialog
            open
            config={postConnectionConfig}
            onComplete={handleConfigComplete}
          />
        ),
        google_search_console: (
          <GoogleSearchConsoleConfigDialog
            open
            config={postConnectionConfig}
            onComplete={handleConfigComplete}
          />
        ),
      }
    : {};

  return (
    <div className="my-2 w-full max-w-md overflow-hidden rounded-xl border border-custom-border bg-white">
      <div className="flex items-center gap-2 px-3.5 py-2.5">
        <Plug className="h-4 w-4 text-gray-600" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-gray-900">Connectors that could help</h3>
      </div>

      {isResolved ? (
        <div className="flex items-center gap-1.5 border-t border-custom-border px-3.5 py-3 text-xs font-medium text-gray-500">
          <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
          {usedApp
            ? `Using ${usedApp.display_name}.`
            : resolution.used
              ? "Connector selected."
              : "Dismissed — none of these were a fit."}
        </div>
      ) : (
        <ul className="flex flex-col border-t border-custom-border">
          {resolved.map(({ app, reason }) => {
            const isConnected = connectedShortNames.has(app.short_name);
            const authLabel = AUTH_METHOD_LABELS[app.auth?.[0]?.type ?? ""] ?? null;
            const isConnectingThis =
              (isConnecting && connectingApp?.short_name === app.short_name) ||
              basicAuthApp?.short_name === app.short_name ||
              apiKeyApp?.short_name === app.short_name ||
              (pendingConnectShortName === app.short_name && !isConnected);
            return (
              <li
                key={app.short_name}
                className="flex items-center gap-2.5 border-b border-custom-border px-3.5 py-2.5"
              >
                <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-custom-border bg-white">
                  <AppLogo shortName={app.short_name} displayName={app.display_name} size="sm" />
                  {isConnected ? (
                    <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-600 ring-2 ring-white">
                      <Check className="h-2 w-2 text-white" aria-hidden="true" />
                    </span>
                  ) : null}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-semibold text-gray-900">{app.display_name}</p>
                    {!isConnected && authLabel ? (
                      <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px] font-medium">
                        {authLabel}
                      </Badge>
                    ) : null}
                  </div>
                  {isConnected ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                      <Check className="h-3 w-3" aria-hidden="true" />
                      Connected
                    </span>
                  ) : reason ? (
                    <p className="truncate text-xs text-gray-500">{reason}</p>
                  ) : null}
                </div>

                {isConnected ? (
                  <span className="flex items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="compact"
                      disabled={isConnectingThis}
                      onClick={() => handleConnect(app)}
                      title="Add another account"
                    >
                      Add account
                    </Button>
                    <Button variant="outline" size="compact" onClick={() => handleUse(app)}>
                      Use
                    </Button>
                  </span>
                ) : (
                  <Button
                    variant="black"
                    size="compact"
                    disabled={isConnectingThis}
                    onClick={() => handleConnect(app)}
                  >
                    {isConnectingThis ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />
                        Connecting
                      </>
                    ) : (
                      "Connect"
                    )}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center justify-between gap-2 bg-custom-bg-subtle px-3.5 py-2">
        <p className="text-xs text-gray-500">
          Looking for something else?{" "}
          <button
            type="button"
            className="font-semibold text-[#f46b10] underline underline-offset-2 hover:opacity-80"
            onClick={() => navigate(CONNECTIONS_ROUTE)}
          >
            Browse all connectors
          </button>
        </p>
        {isResolved ? null : (
          <Button variant="outline" size="compact" onClick={handleDismiss}>
            None of these
          </Button>
        )}
      </div>

      {postConnectionConfig ? configDialogs[postConnectionConfig.appName] ?? null : null}

      <BasicAuthConnectModal
        app={basicAuthApp}
        open={Boolean(basicAuthApp)}
        onOpenChange={(next) => {
          if (!next) closeBasicAuthModal();
        }}
        onSuccess={handleBasicAuthSuccess}
      />

      <ApiKeyConnectModal
        app={apiKeyApp}
        open={Boolean(apiKeyApp)}
        onOpenChange={(next) => {
          if (!next) closeApiKeyModal();
        }}
        onSuccess={handleApiKeySuccess}
      />
    </div>
  );
}
